// The layout engine: template + data → pages of paint primitives in mm. Text is wrapped,
// shrunk or stretched here with the shared metrics, tables are paginated here, and the three
// back ends (ZPL, PDF, SVG) only draw what they are given. Rotated elements come out as a
// `group` with its children in local coordinates, so a back end applies one transform.
import { checkElementString, elementStringHri, elementStringToRaw, parseElementString } from "./gs1.js";
import { fitSize, textWidthMm, wrapText } from "./metrics.js";
import { evalText, renderText } from "./text.js";
const PT = 72 / 25.4; // pt per mm
const sizeMm = (pt) => pt / PT;
/** Resolves `company.logo`-style names (a path into the data) and data URLs to an image source. */
export function imageResolver(data) {
    return (src) => {
        if (src.startsWith("data:"))
            return src;
        let v = data;
        for (const p of src.split(".")) {
            if (typeof v !== "object" || v === null || Array.isArray(v))
                return null;
            v = v[p] ?? null;
        }
        return typeof v === "string" && v.startsWith("data:") ? v : null;
    };
}
export function layout(template, data, opts = {}) {
    const warnings = [];
    const boxes = [];
    const ctx = isObj(data) ? data : {};
    const { width, height } = template.page;
    const isLabelLike = template.elements.every((e) => e.band === "page");
    if (isLabelLike) {
        // One page; a table on a label is drawn where it stands and never continues onto another.
        const page = { width, height, items: [] };
        const pctx = { ...ctx, page: { no: 1, count: 1 } };
        for (const el of template.elements) {
            if (!visible(el, pctx))
                continue;
            boxes.push({ elementId: el.id, page: 0, x: el.x, y: el.y, w: el.w, h: el.h });
            if (el.type === "table") {
                layoutTable(el, pctx, warnings, { page, y: el.y, flowTop: el.y, flowBottom: el.y + el.h + 0.01, newPage: () => page, clip: true });
                continue;
            }
            const items = paintElement(el, pctx, el.w, el.h, warnings, opts);
            place(page, el, items);
        }
        return { pages: [page], warnings, boxes };
    }
    // Documents: page-band elements on every page; flow elements stacked in the body, split over pages.
    const pageEls = template.elements.filter((e) => e.band === "page");
    const flowEls = template.elements.filter((e) => e.band === "flow");
    const m = template.page.margins;
    let flowTop = m.top;
    let flowBottom = height - m.bottom;
    for (const e of pageEls) {
        if (e.anchor === "top")
            flowTop = Math.max(flowTop, e.y + e.h + 2);
        else
            flowBottom = Math.min(flowBottom, height - e.y - e.h - 2);
    }
    if (flowBottom - flowTop < 20) {
        warnings.push({ elementId: null, message: "Sidhuvud och sidfot lämnar inget utrymme för innehållet" });
        flowBottom = Math.max(flowTop + 20, flowBottom);
    }
    const pages = [];
    const newPage = () => { const p = { width, height, items: [] }; pages.push(p); return p; };
    let page = newPage();
    let y = flowTop;
    for (const el of flowEls) {
        if (!visible(el, ctx))
            continue;
        if (el.type === "table") {
            const startPage = pages.indexOf(page), startY = y;
            const r = layoutTable(el, ctx, warnings, { page, y, flowTop, flowBottom, newPage: () => { page = newPage(); return page; } });
            const endPage = pages.indexOf(r.page);
            if (endPage === startPage)
                boxes.push({ elementId: el.id, page: startPage, x: el.x, y: startY, w: el.w, h: Math.max(4, r.y - startY) });
            else {
                boxes.push({ elementId: el.id, page: startPage, x: el.x, y: startY, w: el.w, h: Math.max(4, flowBottom - startY) });
                for (let pi = startPage + 1; pi <= endPage; pi++)
                    boxes.push({ elementId: el.id, page: pi, x: el.x, y: flowTop, w: el.w, h: Math.max(4, (pi === endPage ? r.y : flowBottom) - flowTop) });
            }
            page = r.page;
            y = r.y;
            continue;
        }
        // A flow element keeps its x and width; its height grows with wrapped text.
        const h = el.type === "text" ? Math.max(el.h, textHeight(el, ctx, el.w)) : el.h;
        if (y + h > flowBottom && y > flowTop) {
            page = newPage();
            y = flowTop;
        }
        const items = paintElement(el, ctx, el.w, h, warnings, opts);
        place(page, { ...el, y, h }, items);
        boxes.push({ elementId: el.id, page: pages.indexOf(page), x: el.x, y, w: el.w, h });
        y += h + 1.5;
    }
    // Page-band elements last, with page numbers bound.
    pages.forEach((p, i) => {
        const pctx = { ...ctx, page: { no: i + 1, count: pages.length } };
        for (const el of pageEls) {
            if (!visible(el, pctx))
                continue;
            const yy = el.anchor === "bottom" ? height - el.y - el.h : el.y;
            boxes.push({ elementId: el.id, page: i, x: el.x, y: yy, w: el.w, h: el.h });
            if (el.type === "table") {
                layoutTable(el, pctx, warnings, { page: p, y: yy, flowTop: yy, flowBottom: yy + el.h + 0.01, newPage: () => p, clip: true });
                continue;
            }
            place(p, { ...el, y: yy }, paintElement(el, pctx, el.w, el.h, warnings, opts));
        }
    });
    return { pages, warnings, boxes };
}
function isObj(v) { return typeof v === "object" && v !== null && !Array.isArray(v); }
function visible(el, ctx) {
    if (!el.visibleWhen.trim())
        return true;
    const v = evalText(`{{ ${el.visibleWhen} }}`, ctx);
    return !(v === null || v === false || v === 0 || v === "");
}
/** Put an element's painted items on the page, as a rotated group when it is turned. */
function place(page, el, items) {
    if (items.length === 0)
        return;
    if (el.rotation === 0) {
        for (const it of items)
            page.items.push(shift(it, el.x, el.y));
        return;
    }
    // Local box: for 90/270 the content is laid out in a (h × w) box and turned into the (w × h) frame.
    page.items.push({ kind: "group", x: el.x, y: el.y, w: el.w, h: el.h, rotation: el.rotation, items });
}
function shift(p, dx, dy) {
    switch (p.kind) {
        case "line": return { ...p, x1: p.x1 + dx, y1: p.y1 + dy, x2: p.x2 + dx, y2: p.y2 + dy };
        default: return { ...p, x: p.x + dx, y: p.y + dy };
    }
}
/** Paint one element into a local box of `w × h` at the origin. Rotated elements lay out in the
 *  turned box (h × w for 90/270). */
function paintElement(el, ctx, w, h, warnings, opts) {
    const turned = el.rotation === 90 || el.rotation === 270;
    const lw = turned ? h : w;
    const lh = turned ? w : h;
    switch (el.type) {
        case "text": return paintText(el, ctx, lw, lh);
        case "line": return [{ kind: "line", x1: 0, y1: lh / 2, x2: lw, y2: lh / 2, thickness: el.thickness }];
        case "box": return [{ kind: "rect", x: 0, y: 0, w: lw, h: lh, thickness: el.thickness, fill: el.fill, radius: el.radius }];
        case "image": {
            const src = opts.image ? opts.image(el.src) : el.src.startsWith("data:") ? el.src : null;
            if (!src)
                return [];
            return [{ kind: "image", x: 0, y: 0, w: lw, h: lh, src, threshold: el.threshold }];
        }
        case "barcode": return paintBarcode(el, ctx, lw, lh, warnings);
        case "table": return []; // tables are laid out by layoutTable
    }
}
function textHeight(el, ctx, w) {
    const text = renderText(el.text, ctx);
    const size = el.fit === "shrink" ? fitSize(text, el.font, w, 1e9, el.lineHeight, el.wrap, el.font.size) : el.font.size;
    const lines = el.wrap ? wrapText(text, el.font, size, w) : text.split(/\r?\n/);
    return lines.length * sizeMm(size) * el.lineHeight;
}
function paintText(el, ctx, w, h) {
    const text = renderText(el.text, ctx);
    if (!text)
        return el.inverse ? [{ kind: "rect", x: 0, y: 0, w, h, thickness: 0, fill: true, radius: 0 }] : [];
    let size = el.font.size;
    let scaleX = 1;
    if (el.fit === "shrink")
        size = fitSize(text, el.font, w, h, el.lineHeight, el.wrap, el.font.size);
    else if (el.fit === "fill") {
        // Height fills the box (one line); the glyphs are stretched to the width, within reason.
        size = h * PT / el.lineHeight;
        const natural = textWidthMm(text, el.font, size);
        scaleX = natural > 0 ? Math.min(1.3, w / natural) : 1;
        if (natural * scaleX > w) {
            size = size * (w / (natural * scaleX));
        }
    }
    const lines = el.wrap ? wrapText(text, el.font, size, w) : text.split(/\r?\n/);
    const lineH = sizeMm(size) * el.lineHeight;
    const total = lines.length * lineH;
    const top = el.valign === "top" ? 0 : el.valign === "middle" ? Math.max(0, (h - total) / 2) : Math.max(0, h - total);
    const items = [];
    if (el.inverse)
        items.push({ kind: "rect", x: 0, y: 0, w, h, thickness: 0, fill: true, radius: 0 });
    lines.forEach((line, i) => {
        items.push({ kind: "text", x: 0, y: top + i * lineH, w, h: lineH, text: line, font: el.font, size, align: el.align, scaleX, inverse: el.inverse });
    });
    return items;
}
function paintBarcode(el, ctx, w, h, warnings) {
    const raw = String(evalText(el.value, ctx) ?? "").trim();
    if (!raw)
        return [];
    const gs1 = el.symbology.startsWith("gs1");
    let data = raw;
    let segments = null;
    let hriText = raw;
    if (gs1) {
        segments = parseElementString(raw);
        if (!segments) {
            warnings.push({ elementId: el.id, message: `GS1-data måste skrivas med AI i parentes: (01)…(10)…; fick "${raw.slice(0, 30)}"` });
            return [];
        }
        for (const m of checkElementString(segments))
            warnings.push({ elementId: el.id, message: m });
        data = elementStringToRaw(segments);
        hriText = elementStringHri(segments, el.symbology === "gs1-128");
    }
    else if (el.symbology === "ean13" && !/^\d{12,13}$/.test(raw))
        warnings.push({ elementId: el.id, message: "EAN-13 kräver 12–13 siffror" });
    const hriH = el.hri === "none" ? 0 : sizeMm(el.hriFont.size) * 1.3;
    const barsH = Math.max(1, h - hriH);
    const items = [];
    const barsY = el.hri === "above" ? hriH : 0;
    items.push({ kind: "barcode", x: 0, y: barsY, w, h: barsH, symbology: el.symbology, data, segments, moduleWidth: el.moduleWidth, hri: null });
    if (el.hri !== "none") {
        const hFont = { ...el.hriFont };
        const hsize = fitSize(hriText, hFont, w, hriH, 1.3, false, hFont.size, 4);
        items.push({ kind: "text", x: 0, y: el.hri === "above" ? 0 : barsH, w, h: hriH, text: hriText, font: hFont, size: hsize, align: "center", scaleX: 1, inverse: false });
    }
    return items;
}
function layoutTable(el, ctx, warnings, c) {
    const rowsV = evalText(`{{ ${el.rows} }}`, ctx);
    const rows = Array.isArray(rowsV) ? rowsV : [];
    if (!el.rows.trim())
        warnings.push({ elementId: el.id, message: "Tabellen saknar radkälla" });
    const cols = el.columns.length ? el.columns : [];
    const fixed = cols.reduce((s, col) => s + col.width, 0);
    const free = cols.filter((col) => col.width === 0).length;
    const freeW = free ? Math.max(5, (el.w - fixed) / free) : 0;
    const widths = cols.map((col) => (col.width || freeW));
    const pad = 1.2;
    const lineH = (f) => sizeMm(f.size) * 1.25;
    let page = c.page;
    let y = c.y;
    const grid = el.rules === "grid";
    const drawHeader = () => {
        const hh = Math.max(el.rowHeight || 0, lineH(el.headerFont) + pad);
        let x = el.x;
        cols.forEach((col, i) => {
            page.items.push({ kind: "text", x: x + pad / 2, y: y + pad / 2, w: widths[i] - pad, h: hh - pad, text: renderText(col.header, ctx), font: el.headerFont, size: el.headerFont.size, align: col.align, scaleX: 1, inverse: false });
            if (grid)
                page.items.push({ kind: "rect", x, y, w: widths[i], h: hh, thickness: 0.25, fill: false, radius: 0 });
            x += widths[i];
        });
        if (el.rules !== "none" && !grid)
            page.items.push({ kind: "line", x1: el.x, y1: y + hh, x2: el.x + el.w, y2: y + hh, thickness: 0.4 });
        y += hh;
    };
    if (rows.length === 0) {
        if (el.emptyText) {
            page.items.push({ kind: "text", x: el.x, y, w: el.w, h: lineH(el.font), text: renderText(el.emptyText, ctx), font: el.font, size: el.font.size, align: "left", scaleX: 1, inverse: false });
            y += lineH(el.font) + 1;
        }
        return { page, y };
    }
    if (y + lineH(el.headerFont) + lineH(el.font) * 2 > c.flowBottom && y > c.flowTop) {
        page = c.newPage();
        y = c.flowTop;
    }
    drawHeader();
    // Conditional rows and grouping (owner 2026-11-01): rows the condition drops never count; the rest
    // form groups in order of first appearance, each with an optional header and subtotal line.
    const truthy = (v) => !(v === null || v === false || v === 0 || v === "" || v === "false");
    const kept = el.rowWhen.trim() ? rows.filter((row) => truthy(evalText(`{{ ${el.rowWhen} }}`, { ...ctx, row }))) : rows;
    const groups = [];
    if (el.groupBy.trim()) {
        for (const row of kept) {
            const key = evalText(`{{ ${el.groupBy} }}`, { ...ctx, row });
            const g = groups.find((x) => JSON.stringify(x.key) === JSON.stringify(key));
            if (g)
                g.rows.push(row);
            else
                groups.push({ key, rows: [row] });
        }
    }
    else
        groups.push({ key: null, rows: kept });
    const drawLine = (text, font, indent = 0) => {
        const h = lineH(font) + pad;
        if (y + h > c.flowBottom) {
            if (c.clip)
                return false;
            page = c.newPage();
            y = c.flowTop;
            if (el.repeatHeader)
                drawHeader();
        }
        page.items.push({ kind: "text", x: el.x + pad / 2 + indent, y: y + pad / 2, w: el.w - pad - indent, h: lineH(font), text, font, size: font.size, align: "left", scaleX: 1, inverse: false });
        y += h;
        return true;
    };
    for (const group of groups) {
        const gctx = { ...ctx, group: { key: group.key, count: group.rows.length, rows: group.rows } };
        if (el.groupBy.trim() && el.groupText.trim())
            drawLine(renderText(el.groupText, gctx), { ...el.font, bold: true });
        for (const row of group.rows) {
            const rctx = { ...ctx, row, group: gctx["group"] ?? null };
            const cells = cols.map((col, i) => {
                const text = renderText(col.value, rctx);
                return el.wrap ? wrapText(text, el.font, el.font.size, widths[i] - pad) : [text];
            });
            const subs = [];
            if (el.subRows.trim()) {
                const sv = evalText(`{{ ${el.subRows} }}`, rctx);
                const list = Array.isArray(sv) ? sv : sv === null || sv === "" ? [] : [sv];
                for (const sub of list)
                    subs.push(renderText(el.subRowText, { ...rctx, sub }));
            }
            const nLines = Math.max(1, ...cells.map((cl) => cl.length));
            const rowH = Math.max(el.rowHeight || 0, nLines * lineH(el.font) + pad) + subs.length * lineH(el.font);
            if (y + rowH > c.flowBottom) {
                if (c.clip) {
                    warnings.push({ elementId: el.id, message: `Tabellen rymmer inte alla rader (${rows.length})` });
                    break;
                }
                page = c.newPage();
                y = c.flowTop;
                if (el.repeatHeader)
                    drawHeader();
            }
            let x = el.x;
            cols.forEach((col, i) => {
                cells[i].forEach((line, li) => {
                    page.items.push({ kind: "text", x: x + pad / 2, y: y + pad / 2 + li * lineH(el.font), w: widths[i] - pad, h: lineH(el.font), text: line, font: el.font, size: el.font.size, align: col.align, scaleX: 1, inverse: false });
                });
                if (grid)
                    page.items.push({ kind: "rect", x, y, w: widths[i], h: rowH, thickness: 0.25, fill: false, radius: 0 });
                x += widths[i];
            });
            subs.forEach((s, si) => {
                page.items.push({ kind: "text", x: el.x + (widths[0] ?? 0) + pad * 2, y: y + pad / 2 + nLines * lineH(el.font) + si * lineH(el.font), w: el.w - (widths[0] ?? 0) - pad * 2, h: lineH(el.font), text: s, font: { ...el.font, size: Math.max(5, el.font.size - 1) }, size: Math.max(5, el.font.size - 1), align: "left", scaleX: 1, inverse: false });
            });
            y += rowH;
            if (el.rules === "rows")
                page.items.push({ kind: "line", x1: el.x, y1: y, x2: el.x + el.w, y2: y, thickness: 0.15 });
        }
        if (el.groupBy.trim() && el.subtotalText.trim()) {
            drawLine(renderText(el.subtotalText, gctx), { ...el.font, bold: true }, el.w * 0.4);
            if (el.rules !== "none")
                page.items.push({ kind: "line", x1: el.x, y1: y, x2: el.x + el.w, y2: y, thickness: 0.3 });
        }
    }
    return { page, y: y + 2 };
}
//# sourceMappingURL=layout.js.map